import { navigate } from "gatsby"
import { useState, useEffect, useRef, forwardRef } from "react"
import { Box, Typography, type BoxProps, useMediaQuery } from "@mui/material"
import { useTheme } from "@mui/material/styles"
import { gsap } from "@ggs/ui/local/gsap"

import type { FC, iconKeys } from ".."
import { Button, SvgIcon, Link } from ".."
import useSearchForm from "../../local/hooks/useSearchForm"

type AutoCompleteSearchProps = {
  onClose: () => void
}

type ResultItem = {
  title: string
  uri: string | null
}

const AutoCompleteSearch: FC<AutoCompleteSearchProps> = ({ onClose }) => {
  const theme = useTheme()
  const isMobile = useMediaQuery(theme.breakpoints.down("md"))
  const { getAutocompleteResults } = useSearchForm()
  const [inputValue, setInputValue] = useState("")
  const [results, setResults] = useState<ResultItem[]>([])
  const inputRefContainer = useRef<HTMLInputElement>(null)
  const inputRef = useRef<HTMLInputElement>(null)
  const placeholderText = isMobile
    ? "Search for trips and inspiration"
    : "Search for trips and travel inspiration"
  const isResults = results.length > 0
  const DEBOUNCE_DELAY = 200

  const handleInputChange = (event: any) => {
    setInputValue(event?.target?.value)
  }

  const handleFetchResults = async (keyword: string) => {
    const data = await getAutocompleteResults(keyword)
    setResults(data as ResultItem[])
  }

  useEffect(() => {
    const handler = setTimeout(() => {
      if (inputValue) {
        handleFetchResults(inputValue)
      } else {
        setResults([])
      }
    }, DEBOUNCE_DELAY)

    // Clean up the timeout if inputValue changes before the delay ends
    return () => {
      clearTimeout(handler)
    }
  }, [inputValue])

  // Effect to handle click outside to close the overlay
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (inputRefContainer.current && !inputRefContainer.current.contains(event.target as Node)) {
        onClose()
      }
    }
    document.addEventListener("mousedown", handleClickOutside)

    const ctx = gsap.from(inputRefContainer.current, {
      ...(isMobile ? { x: () => "+=50%", duration: 0.35 } : { y: () => "+=20", duration: 0.2 }),
      opacity: 0,
      scrollTrigger: {
        trigger: inputRefContainer.current,
        start: "top bottom-=20",
      },
    })

    return () => {
      document.removeEventListener("mousedown", handleClickOutside)
      ctx.revert()
    }
  }, [inputRefContainer])

  useEffect(() => {
    document.body.style.overflow = "hidden"

    return () => {
      document.body.style.overflow = "auto" // Enable scroll when overlay is closed
    }
  }, [])

  useEffect(() => {
    if (inputRef.current) {
      inputRef.current.focus() // Focus input when component renders
    }
  }, [inputRef])

  return (
    <Box
      sx={{
        position: "fixed",
        top: 0,
        left: 0,
        width: "100vw",
        height: "100vh",
        backgroundColor: "rgba(0, 0, 0, 0.5)",
        display: "flex",
        justifyContent: "center",
        alignItems: "flex-start",
        zIndex: 1000,
      }}
    >
      <Box ref={inputRefContainer} position="relative" mt={{ xs: "10px", md: "84px" }}>
        <InputField
          ref={inputRef}
          icon="search"
          placeholder={placeholderText}
          onChange={handleInputChange}
          value={inputValue}
          onClearInput={() => setInputValue("")}
          buttonLabel={isResults ? "More Results" : "Show Results"}
        />
        {isResults && <SearchQuickResults results={results} />}
      </Box>
    </Box>
  )
}

export default AutoCompleteSearch

type InputFieldProps = {
  icon: string
  onClearInput: () => void
  buttonLabel: string
} & BoxProps<"input">

const InputField = forwardRef<HTMLInputElement, InputFieldProps>(
  ({ icon, onClearInput, buttonLabel, ...props }, ref) => {
    const onHandleSearch = () => {
      if (props.value) {
        navigate(`/search?keyword=${props.value}`)
      } else {
        navigate("/search")
      }
    }
    return (
      <Box
        position="relative"
        sx={(theme) => ({
          maxWidth: "1000px",
          width: "calc(100vw - 20px)",
          border: `1px solid ${theme.palette["neutral.200"].main}`,
          bgcolor: theme.palette["neutral.100"].main,
          borderRadius: { xs: 1.5, md: 2 },
          boxShadow: `0px 0px 25px 0px rgba(0, 0, 0, 0.10)`,
          display: "flex",
          alignItems: "center",
        })}
      >
        <Box
          component="input"
          type="text"
          ref={ref}
          onKeyDown={(e: React.KeyboardEvent<HTMLInputElement>) => {
            if (e.key === "Enter") {
              e.preventDefault()
              onHandleSearch()
            }
          }}
          sx={(theme) => ({
            ...theme.typography["body.lg"],
            "fontSize": { xs: "16px!important", md: "18px" },
            "border": 0,
            "borderRadius": { xs: 1.5, md: 2 },
            "bgcolor": theme.palette["neutral.100"].main,
            "color": theme.palette["neutral.900"].main,
            "fontWeight": 400,
            "height": { xs: 56, md: 77 },
            "opacity": 0.7,
            "p": { xs: 2, md: 4 },
            "pl": { xs: 11, md: 16.5 },
            "width": "100%",
            "&::placeholder": {
              color: "rgba(55, 54, 56, 0.60)",
              fontWeight: 400,
              opacity: 0.7,
            },

            "&:focus": {
              outline: "none",
            },
          })}
          {...props}
        />
        <SvgIcon
          variant={icon as iconKeys}
          size={{ xs: 16, md: 20 }}
          strokeMode
          sx={{
            position: "absolute",
            left: { xs: 15, md: 25 },
            top: "50%",
            transform: "translateY(-50%)",
            stroke: (theme) => theme.palette["neutral.800"].main,
            zIndex: 1,
          }}
        />
        <Box
          role="button"
          aria-label="Clear input"
          tabIndex={0}
          onClick={onClearInput}
          onKeyDown={(e) => {
            if (e.key === "Enter") {
              e.preventDefault()
              onClearInput()
            }
          }}
          display="flex"
          sx={{ cursor: "pointer" }}
        >
          <SvgIcon
            variant={"close"}
            size={12}
            sx={{
              position: "absolute",
              right: { xs: 137, md: 195 },
              top: "50%",
              transform: "translateY(-50%)",
              zIndex: 1,
            }}
          />
        </Box>
        <Button
          aria-label="Search"
          sx={{
            bgcolor: "#DAE7F1",
            minHeight: { xs: "40px!important", md: "53px!important" },
            px: { xs: 4.5, md: 8 },
            py: { md: 2 },
            mr: { xs: 2, md: 3 },
            whiteSpace: "nowrap",
          }}
          onClick={onHandleSearch}
          onKeyDown={(e) => {
            if (e.key === "Enter") {
              e.preventDefault()
              onHandleSearch()
            }
          }}
        >
          {buttonLabel}
        </Button>
      </Box>
    )
  }
)
InputField.displayName = "InputField"

// A sub component for the results
const SearchQuickResults: FC<{ results: ResultItem[] }> = ({ results }) => {
  const defaultUrl = "/search"
  return (
    <Box
      sx={(theme) => ({
        position: "absolute",
        top: { xs: 65, md: 85 },
        left: 0,
        width: "100%",
        bgcolor: "rgba(255, 255, 255, 0.98)",
        boxShadow: "0px 0px 25px 0px rgba(0, 0, 0, 0.10)",
        backdropFilter: "blur(10px)",
        borderRadius: 2,
        padding: { xs: 2, md: theme.spacing(2, 5) },
        zIndex: 2,
      })}
    >
      {results.map((result, index) => (
        <Box
          key={index}
          p={3}
          sx={(theme) => ({
            "transition": theme.transitions.create(["background-color", "color", "opacity"]),
            "height": { xs: 54, md: 63 },
            "display": "flex",
            "alignItems": "center",
            "px": 4,
            "borderRadius": 1.5,
            "cursor": "pointer",
            "position": "relative",
            "&:hover": {
              "bgcolor": "rgba(35, 53, 88, 0.06)",
              "& span": {
                color: theme.palette["secondary.800"].main,
                opacity: 1,
              },
            },
          })}
        >
          <Link
            type="overlay"
            href={result?.uri ?? defaultUrl}
            aria-label={result?.title}
            title={result?.title}
            zIndex={1}
            sx={{ cursor: "pointer" }}
          />
          <Typography
            variant="link.md"
            sx={(theme) => ({ color: theme.palette["neutral.800"].main, opacity: 0.7 })}
          >
            {result.title}
          </Typography>
        </Box>
      ))}
    </Box>
  )
}
